import Bugsnag from '@bugsnag/js';
import { useOnlineStatus } from '../hooks/offline-hooks/offline-misc';
import { useInspection } from '../hooks/offline-hooks/createInspectionhook';
import { useUserIndexedDB } from '../hooks/offline-hooks/userMutationIndexedDB';
import { useNavigate } from 'react-router-dom';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { isValidUUID } from '../utilities/offline_hooks_setup_apollo/offlineUtils';
import { inputTypes } from '../utilities/inspection';
import { get } from 'lodash';
import { getRoute, paths } from '../constants/strings';
import React from 'react';
import pjson from '../../package.json';
import { useWorkspace } from './WorkspaceProvider';
import {
  getOfflineFile,
  deleteOfflineFile,
} from '../hooks/offline-hooks/localForageDb';
import { createOfflineClient } from '../graphql/offline_client';
import { useAuth0 } from '@auth0/auth0-react';
import { useMyInspections } from './MyInspectionsProvider';
import { useQueryParams } from '../hooks/misc';

const REQUEST_DEBOUNCE = 250;

const MutationQueueContext = createContext();

export const useCachedMutationFlush = (user) => {
  const { workspace } = useWorkspace();
  const { getAccessTokenSilently } = useAuth0();
  const offlineClient = createOfflineClient(getAccessTokenSilently);
  const online = useOnlineStatus();
  const currentUser = user;
  const { uploadImageFile, uploadAudioFile } = useInspection();
  const packageVersion = pjson.version.split('.');
  const {
    isMutationDBOpen,
    dequeueMutation,
    updateQueueWithDeepReplace,
    getQueueCount,
  } = useUserIndexedDB(
    `userSpecificMutation-${packageVersion[0]}.${packageVersion[1]}`,
  );
  const [progress, setProgress] = useState({ total: 0, processed: 0 });
  const navigate = useNavigate();
  const mutationDBOpen = useMemo(() => isMutationDBOpen(), [isMutationDBOpen]);
  const [queueIsFlushing, setQueueIsFlushing] = useState(false);
  const { refetchMyInspections } = useMyInspections();
  const { upsertParam, deleteParam } = useQueryParams();

  useEffect(() => {
    if (!!queueIsFlushing) {
      upsertParam('sync', 'TRUE');
    } else {
      setTimeout(() => {
        deleteParam('sync');
      }, 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueIsFlushing]);

  const processUUIDSStringToURL = (uuidString, isImage, isAudio) => {
    return new Promise(async (resolve, reject) => {
      try {
        const elements = uuidString.split('|');
        const newElements = [];
        for (let index = 0; index < elements.length; index++) {
          let element = elements[index];
          if (isValidUUID(element) && isImage) {
            const fileBlob = await getOfflineFile(element);
            const link = await uploadImageFile(
              element,
              fileBlob,
              '',
              () => {},
              true,
              null,
              null,
              null,
            );
            await deleteOfflineFile(element).catch(() => {});
            newElements.push(link);
          } else if (isValidUUID(element) && isAudio) {
            const fileBlob = await getOfflineFile(element);
            const audioFileDetails = {
              blob: fileBlob,
              fileUUID: element,
              onSubmit: () => {},
              transcriptRef: null,
              recordingName: null,
              setFile: () => {},
              setLoading: () => {},
            };
            const audioURL = await uploadAudioFile(audioFileDetails);
            await deleteOfflineFile(element).catch(() => {});
            newElements.push(audioURL);
          } else {
            newElements.push(element);
          }
        }
        const newString = newElements.join('|');
        resolve(newString);
      } catch (error) {
        reject(error);
      }
    });
  };

  const processUUIDObservationsToURL = (observationImages) => {
    return new Promise(async (resolve, reject) => {
      try {
        const urlObservationImages = [];
        for (let observationImage of observationImages) {
          if (isValidUUID(observationImage)) {
            const fileBlob = await getOfflineFile(observationImage);
            const link = await uploadImageFile(
              observationImage,
              fileBlob,
              '',
              () => {},
              true,
              null,
              null,
              null,
            );
            await deleteOfflineFile(observationImage).catch(() => {});
            urlObservationImages.push(link);
          } else {
            urlObservationImages.push(observationImage);
          }
        }
        resolve(urlObservationImages);
      } catch (error) {
        reject(error);
      }
    });
  };

  const processQueueItem = async (item) => {
    const { document, options, mock } = item;
    try {
      if (
        !!mock?.inputType &&
        (mock.inputType === inputTypes.image ||
          mock.inputType === inputTypes.multiImage ||
          mock.inputType === inputTypes.signature ||
          mock.inputType === inputTypes.multiSignature ||
          mock.inputType === inputTypes.file ||
          mock.inputType === inputTypes.multiFile)
      ) {
        // If there's an offline image then upload it first to replace UUID with URL.
        const newValue = await processUUIDSStringToURL(
          options.variables.value,
          true,
          false,
        );
        options.variables.value = newValue;
      }

      if (
        mock?.inputType === inputTypes.audio &&
        isValidUUID(options.variables.additional)
      ) {
        const newAudioValue = await processUUIDSStringToURL(
          options.variables.additional,
          false,
          true,
        );
        options.variables.additional = newAudioValue;
      }

      if (options.variables.failImage) {
        const newFailImage = await processUUIDSStringToURL(
          options.variables.failImage,
          true,
          false,
        );
        options.variables.failImage = newFailImage;
      }

      if (
        mock?.__typename === 'Observation' &&
        options.variables.images.length
      ) {
        const newObservationImagesURL = await processUUIDObservationsToURL(
          options.variables.images,
        );
        options.variables.images = newObservationImagesURL;
      }

      const { data, errors } = await offlineClient.mutate({
        mutation: document,
        ...options,
        context: {
          ...options.context,
          headers: {
            ...options.context?.headers,
            // Recorded in the API's audit_log as when the user made the change.
            ...(item.queuedAt && { 'x-offline-queued-at': item.queuedAt }),
          },
        },
      });

      if (!errors) {
        if (item.hasMultipleItemIds) {
          return data;
        } else if (item.newIdPath) {
          const newId = get(data, item.newIdPath);
          if (!newId) {
            throw new Error('Could not fetch new ID');
          }
          return { ...item, id: newId };
        }
      }
    } catch (error) {
      console.error('Error processing mutation:', error);
    }
    return null;
  };

  const processQueue = async () => {
    if (!online || !mutationDBOpen || queueIsFlushing) {
      return;
    }

    try {
      const parts = window.location.pathname.split('/');
      const isUuid = parts[parts.length - 1];
      if (isValidUUID(isUuid) && online) {
        navigate(getRoute(workspace.id, paths.dashboard));
      }
      const total = (await getQueueCount(currentUser.id)) || 0;
      setProgress({ total, processed: 0 });

      await new Promise((r) => setTimeout(r, REQUEST_DEBOUNCE));
      let nextRequest = await dequeueMutation(currentUser.id);
      if (nextRequest) setQueueIsFlushing(true);
      while (nextRequest) {
        await new Promise((r) => setTimeout(r, REQUEST_DEBOUNCE));
        const updatedRequest = await processQueueItem(nextRequest);
        if (updatedRequest?.submitBundle) {
          for (const [
            i,
            newItem,
          ] of updatedRequest.submitBundle.items.entries()) {
            await updateQueueWithDeepReplace(
              currentUser.id,
              nextRequest.mock.items[i].id,
              newItem.id,
            );
            await updateQueueWithDeepReplace(
              currentUser.id,
              nextRequest.mock.items[i].bundleId,
              newItem.bundleId,
            );
          }
        }
        if (updatedRequest && !updatedRequest?.submitBundle) {
          await updateQueueWithDeepReplace(
            currentUser.id,
            nextRequest.id,
            updatedRequest.id,
          );
          if (window.location.pathname.includes(nextRequest.id)) {
            navigate(
              getRoute(
                nextRequest.mock.workspaceId,
                paths.assessment,
                updatedRequest.id,
              ),
            );
          }
        }
        setProgress((p) => ({ total: p.total, processed: p.processed + 1 }));
        nextRequest = await dequeueMutation(currentUser.id);
        if (!nextRequest) {
          break;
        }
      }
    } finally {
      setQueueIsFlushing(false);
      refetchMyInspections();
    }
  };
  const percent =
    progress.total > 0
      ? Math.round((progress.processed / progress.total) * 100)
      : null;

  useEffect(() => {
    if (online && mutationDBOpen && !queueIsFlushing) processQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online, mutationDBOpen]);

  useEffect(() => {
    if (!mutationDBOpen || !currentUser?.id) return;
    getQueueCount(currentUser.id).then((count) => {
      const pendingMutations = count || 0;
      Bugsnag.leaveBreadcrumb(
        'Offline mutation queue status',
        { pendingMutations },
        'state',
      );
      Bugsnag.addMetadata('offlineQueue', { pendingMutations });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutationDBOpen, currentUser?.id]);

  return { queueIsFlushing, progress, percent };
};

export const MutationQueueProvider = ({ user, children }) => {
  const value = useCachedMutationFlush(user);

  return (
    <MutationQueueContext.Provider value={value}>
      {children}
    </MutationQueueContext.Provider>
  );
};
export const useMutationQueue = () => useContext(MutationQueueContext);
